import type { ReactElement } from 'react';
import { EmptyState } from '@/design/EmptyState';
import { distinctVehicleNames } from '@/features/fleet/labels';

/**
 * A year, as twelve small months.
 *
 * The first attempt drew every booking as a bar across one long timeline, and
 * it could not have worked: across a year a three-day hire is under one per
 * cent of the width — about fifteen pixels — so every label collapsed to "P"
 * or "F", and ten lanes of slivers piled up in whichever fortnight the data
 * happened to be in. At this distance individual bookings are not readable,
 * so the year has to answer a coarser question instead.
 *
 * The question it answers is **how much of the fleet is committed on any given
 * day**, which is what somebody asks when a guest wants a car for a fortnight
 * in March. One shade per day, twelve months on a screen, weekday columns kept
 * — a rental's weeks are not flat and a Saturday needs to be findable.
 *
 * Which vehicle is on hover, and clicking a day opens that week in the ordinary
 * grid, where the vehicle rows live. Nothing is lost; it moves one click away.
 */

const DAY_MS = 86_400_000;

/** Monday first: this is a working calendar, and the weekend belongs together. */
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;

export interface YearBar {
  readonly vehicle: string;
  readonly start_at: string;
  readonly end_at: string;
}

interface Props {
  readonly bars: readonly YearBar[];
  readonly vehicles: readonly {
    readonly id: string;
    readonly plate: string;
    readonly name: string;
  }[];
  /** Midnight of the first day on screen. */
  readonly windowStart: number;
  readonly days: number;
  /** Jump to the ordinary grid at this day. */
  readonly onPickDay: (day: Date) => void;
}

function midnight(value: number | string): number {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/**
 * A sequential ramp on the one accent colour, light to dark.
 *
 * One hue, because this is magnitude and not identity — and because the house
 * rule is one saturated colour spent where it means something. A day with
 * nothing out is left as plain paper rather than given its own shade: empty is
 * the background state, and giving it ink makes a quiet January look busy.
 */
function shadeFor(out: number, fleet: number): string {
  if (out === 0) return 'bg-paper text-ink-faint';
  const share = fleet === 0 ? 1 : out / fleet;
  if (share < 0.34) return 'bg-signal/20 text-ink';
  if (share < 0.67) return 'bg-signal/45 text-ink';
  if (share < 1) return 'bg-signal/70 text-ink-inverse';
  return 'bg-signal text-ink-inverse';
}

export function CalendarYear({
  bars,
  vehicles,
  windowStart,
  days,
  onPickDay,
}: Props): ReactElement {
  const fleet = vehicles.length;
  const plates = distinctVehicleNames(vehicles);

  /**
   * Which vehicles are out on each day, keyed by the day's timestamp.
   *
   * Walked once per booking rather than once per day: a year is 365 days and
   * checking every booking against every one of them is the sort of loop that
   * is fine with ten bookings and noticeable with four hundred.
   */
  const outOn = new Map<number, Set<string>>();
  for (const bar of bars) {
    const from = Math.max(midnight(bar.start_at), windowStart);
    const until = Math.min(midnight(bar.end_at), windowStart + (days - 1) * DAY_MS);
    for (let day = from; day <= until; day += DAY_MS) {
      const already = outOn.get(day);
      if (already === undefined) outOn.set(day, new Set([bar.vehicle]));
      else already.add(bar.vehicle);
    }
  }

  // The months the window covers, each with the days that fall in it.
  const months: { label: string; start: Date; days: Date[] }[] = [];
  for (let index = 0; index < days; index += 1) {
    const date = new Date(windowStart + index * DAY_MS);
    const label = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const last = months[months.length - 1];
    if (last !== undefined && last.label === label) last.days.push(date);
    else months.push({ label, start: date, days: [date] });
  }

  if (fleet === 0) {
    return (
      <EmptyState
        headline="No vehicles to show"
        detail="Add vehicles and the year fills in: each day is shaded by how much of the fleet is committed."
      />
    );
  }

  const today = midnight(Date.now());

  return (
    <div className="hidden md:block">
      <div className="grid gap-x-6 gap-y-5 px-4 py-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {months.map((month) => {
          // Monday-first offset, so the 1st lands under the right weekday.
          const first = month.days[0]!;
          const lead = (first.getDay() + 6) % 7;

          return (
            <div key={month.label}>
              <p className="mb-1.5 text-micro font-semibold uppercase tracking-wide text-ink-muted">
                {month.label}
              </p>

              <div className="grid grid-cols-7 gap-px">
                {WEEKDAYS.map((letter, index) => (
                  <span
                    key={`${letter}-${String(index)}`}
                    className="pb-0.5 text-center text-micro text-ink-faint"
                  >
                    {letter}
                  </span>
                ))}

                {Array.from({ length: lead }, (_, index) => (
                  <span key={`lead-${String(index)}`} />
                ))}

                {month.days.map((date) => {
                  const stamp = date.getTime();
                  const out = outOn.get(stamp);
                  const count = out?.size ?? 0;
                  const isToday = stamp === today;

                  return (
                    <button
                      key={stamp}
                      type="button"
                      onClick={() => onPickDay(date)}
                      title={
                        count === 0
                          ? `${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })} — the whole fleet is free`
                          : `${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })} — ${String(count)} of ${String(fleet)} out: ${[...(out ?? [])]
                              .map((id) => plates.get(id) ?? '—')
                              .join(', ')}`
                      }
                      className={[
                        'tnum aspect-square rounded-[2px] text-micro leading-none',
                        'hover:ring-1 hover:ring-ink focus:outline-none focus:ring-1 focus:ring-ink',
                        shadeFor(count, fleet),
                        isToday ? 'ring-1 ring-alert' : '',
                      ].join(' ')}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* The scale, in words. A shade nobody can name is decoration. */}
      <div className="flex flex-wrap items-center gap-4 border-t border-rule px-4 py-3 text-small text-ink-muted">
        <span className="flex items-center gap-2">
          <span className="h-3 w-5 rounded-[2px] border border-rule bg-paper" />
          All free
        </span>
        <span className="flex items-center gap-2">
          <span className="h-3 w-5 rounded-[2px] bg-signal/20" />
          A few out
        </span>
        <span className="flex items-center gap-2">
          <span className="h-3 w-5 rounded-[2px] bg-signal/45" />
          About half
        </span>
        <span className="flex items-center gap-2">
          <span className="h-3 w-5 rounded-[2px] bg-signal/70" />
          Most out
        </span>
        <span className="flex items-center gap-2">
          <span className="h-3 w-5 rounded-[2px] bg-signal" />
          Fully booked
        </span>
        <span className="text-ink-faint">Hover a day for the vehicles. Click to open its week.</span>
      </div>
    </div>
  );
}
